/*
 * Eureka wishlist - client-side store + UI controllers.
 *
 * MECHANISM (driver/merchant decision 2026-07-24): customer metafield + localStorage,
 * implemented theme-native as:
 *   - localStorage is the WRITABLE source of truth (works for guests AND logged-in
 *     customers). Entries are stored under the key `theme:wishlist` as a JSON list of
 *     { pid, handle, id (variant id | null), qty }.
 *   - The customer metafield `custom.wishlist` is READ defensively on boot to hydrate
 *     the store when a logged-in customer has a merchant-provisioned/synced list. The
 *     metafield is injected by Liquid ONLY when it exists (guarded) - see
 *     `sections/main-wishlist.liquid`. It currently does NOT exist in the store (no
 *     CUSTOMER metafield definition), so hydration degrades gracefully to empty.
 *   - We DO NOT write customer metafields and perform NO store mutation. A Liquid theme
 *     cannot write customer metafields client-side, and creating the definition is a
 *     store mutation (safety gate). Metafield WRITE-BACK / cross-device sync is an infra
 *     follow-up: it needs a `custom.wishlist` CUSTOMER metafield definition (storefront
 *     read access) plus an app proxy or the Customer Account API. The READ path below is
 *     wired now so it activates the moment the merchant provisions the metafield.
 */
(function () {
  'use strict';

  if (window.__wishlistInit) return;
  window.__wishlistInit = true;

  var KEY = 'theme:wishlist';
  var EVENT = 'wishlist:updated';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function normalizeEntry(raw) {
    if (raw == null) return null;
    if (typeof raw === 'object') {
      var pid = raw.pid || raw.product_id || raw.productId || raw.id;
      if (!pid) return null;
      return {
        pid: String(pid),
        handle: raw.handle || '',
        id: raw.id != null && raw.id !== '' ? String(raw.id) : null,
        qty: parseInt(raw.qty, 10) > 0 ? parseInt(raw.qty, 10) : 1,
      };
    }
    return { pid: String(raw), handle: '', id: null, qty: 1 };
  }

  var Store = {
    read: function () {
      try {
        var list = JSON.parse(localStorage.getItem(KEY) || '[]');
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },
    write: function (list) {
      try {
        localStorage.setItem(KEY, JSON.stringify(list));
      } catch (e) {
        /* storage full / unavailable - degrade silently */
      }
      document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: list } }));
    },
    items: function () {
      return this.read();
    },
    count: function () {
      return this.read().length;
    },
    hasProduct: function (pid) {
      return this.read().some(function (i) {
        return String(i.pid) === String(pid);
      });
    },
    getProduct: function (pid) {
      return this.read().filter(function (i) {
        return String(i.pid) === String(pid);
      })[0];
    },
    toggleProduct: function (raw) {
      var entry = normalizeEntry(raw);
      if (!entry) return false;
      var list = this.read();
      var idx = list.findIndex(function (i) {
        return String(i.pid) === String(entry.pid);
      });
      if (idx > -1) {
        list.splice(idx, 1);
        this.write(list);
        return false;
      }
      list.push(entry);
      this.write(list);
      return true;
    },
    upsertVariant: function (raw) {
      var entry = normalizeEntry(raw);
      if (!entry) return;
      var list = this.read();
      var idx = list.findIndex(function (i) {
        return String(i.pid) === String(entry.pid);
      });
      if (idx > -1) {
        list[idx] = Object.assign({}, list[idx], entry);
      } else {
        list.push(entry);
      }
      this.write(list);
    },
    removeProduct: function (pid) {
      var list = this.read().filter(function (i) {
        return String(i.pid) !== String(pid);
      });
      this.write(list);
    },
    hydrateFromMetafield: function (mfList) {
      if (!Array.isArray(mfList) || !mfList.length) return;
      var list = this.read();
      var seen = {};
      list.forEach(function (i) {
        seen[String(i.pid)] = true;
      });
      var changed = false;
      mfList.forEach(function (raw) {
        var entry = normalizeEntry(raw);
        if (entry && !seen[String(entry.pid)]) {
          list.push(entry);
          seen[String(entry.pid)] = true;
          changed = true;
        }
      });
      if (changed) this.write(list);
    },
  };
  window.WishlistStore = Store;

  /* Reflect the pressed state of every card heart against the store. */
  function reflectToggles(scope) {
    var container = scope || document;
    container.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var pid = btn.getAttribute('data-product-id');
      btn.setAttribute('aria-pressed', Store.hasProduct(pid) ? 'true' : 'false');
    });
  }

  /* Single delegated owner for the card heart toggle.
     - Already saved -> remove directly (no drawer needed to remove).
     - Not saved, product needs a variant (size/colour) and the Add drawer exists ->
       open the add-size-select drawer so the shopper picks a size before saving.
     - Otherwise (single-variant product) -> add directly. */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-wishlist-toggle]');
    if (!toggle) return;
    e.preventDefault();
    var pid = toggle.getAttribute('data-product-id');
    if (
      !Store.hasProduct(pid) &&
      toggle.getAttribute('data-wl-requires-variant') === 'true' &&
      document.getElementById(DRAWERS.add)
    ) {
      openWishlistDrawer('add', toggle);
      return;
    }
    /* Optimistic wishlist-tile removal: dim + freeze the tile before the
       store round-trip discards it (wishlist-item-card, brief §5). */
    if (Store.hasProduct(pid)) {
      var tile = toggle.closest('[data-wl-tile-root]');
      if (tile) tile.setAttribute('data-wl-removing', '');
    }
    var added = Store.toggleProduct({
      pid: pid,
      handle: toggle.getAttribute('data-product-handle') || '',
      id: toggle.getAttribute('data-variant-id') || null,
    });
    toggle.setAttribute('aria-pressed', added ? 'true' : 'false');
  });

  /* Keep hearts + counts in sync after any store change. */
  document.addEventListener(EVENT, function () {
    reflectToggles(document);
  });

  /* Live count badge (header). */
  if (!customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class extends HTMLElement {
        connectedCallback() {
          this._render();
          this._onUpdate = this._render.bind(this);
          document.addEventListener(EVENT, this._onUpdate);
        }
        disconnectedCallback() {
          document.removeEventListener(EVENT, this._onUpdate);
        }
        _render() {
          var count = Store.count();
          this.textContent = count > 99 ? '99+' : String(count);
          if (count > 0) {
            this.removeAttribute('hidden');
          } else {
            this.setAttribute('hidden', '');
          }
        }
      }
    );
  }

  /* Fetch a section's rendered HTML for a product via the Section Rendering API.
     QA P1 fix: pass the stored variant id so Liquid renders the SAVED
     variant's option values/price/media — without it the tile showed the
     first-available variant while Add posted the saved one. */
  function fetchProductSection(handle, sectionId, variantId) {
    var url = root() + 'products/' + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(sectionId);
    if (variantId) url += '&variant=' + encodeURIComponent(variantId);
    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
      return res.text();
    });
  }

  function parseFragment(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  /* Wishlist page grid - hydrates each stored item into a real product tile. */
  if (!customElements.get('wishlist-page')) {
    customElements.define(
      'wishlist-page',
      class extends HTMLElement {
        connectedCallback() {
          this.grid = this.querySelector('[data-wl-grid]');
          this.emptyState = this.querySelector('[data-wl-empty]');
          this.loadingEl = this.querySelector('[data-wl-loading]');
          this.tileSection = this.getAttribute('data-tile-section');
          this._onUpdate = this._onStoreChange.bind(this);
          this._rendered = '';
          this.render();
          document.addEventListener(EVENT, this._onUpdate);
        }
        disconnectedCallback() {
          document.removeEventListener(EVENT, this._onUpdate);
        }
        _onStoreChange() {
          /* Only re-render when the set of product ids actually changed. */
          var signature = Store.items()
            .map(function (i) {
              return i.pid + ':' + (i.id || '');
            })
            .join(',');
          if (signature !== this._rendered) this.render();
        }
        render() {
          var items = Store.items();
          this._rendered = items
            .map(function (i) {
              return i.pid + ':' + (i.id || '');
            })
            .join(',');

          if (!items.length) {
            if (this.grid) this.grid.innerHTML = '';
            if (this.emptyState) this.emptyState.removeAttribute('hidden');
            if (this.loadingEl) this.loadingEl.setAttribute('hidden', '');
            return;
          }
          if (this.emptyState) this.emptyState.setAttribute('hidden', '');
          if (!this.grid || !this.tileSection) return;
          if (this.loadingEl) this.loadingEl.removeAttribute('hidden');

          var self = this;
          var withHandle = items.filter(function (i) {
            return i.handle;
          });
          Promise.all(
            withHandle.map(function (item) {
              return fetchProductSection(item.handle, self.tileSection, item.id)
                .then(function (html) {
                  var node = parseFragment(html, '[data-wl-tile-root]');
                  return node ? { item: item, node: node } : null;
                })
                .catch(function () {
                  return null;
                });
            })
          ).then(function (results) {
            self.grid.innerHTML = '';
            results.forEach(function (result) {
              if (!result) return;
              var li = document.createElement('li');
              li.className = 'wishlist__item';
              li.appendChild(result.node);
              /* Apply the stored variant to the tile once mounted. */
              if (result.item.id) {
                var idInput = li.querySelector('.product-variant-id, [data-wl-tile-variant-id]');
                if (idInput && 'value' in idInput) idInput.value = result.item.id;
                li.querySelectorAll('[data-wl-edit], [data-wl-tile-root]').forEach(function (el) {
                  el.setAttribute('data-variant-id', result.item.id);
                });
              } else {
                /* select-required: entry saved without a variant (id: null).
                   Drop the variant row (no empty band) and re-route the Add
                   button to the Add drawer instead of submitting a null
                   variant. */
                var tileRoot = li.querySelector('[data-wl-tile-root]');
                if (tileRoot) tileRoot.setAttribute('data-wl-needs-variant', '');
                var variantRow = li.querySelector('.wishlist-tile__variant');
                if (variantRow) variantRow.remove();
                var addBtn = li.querySelector('.wishlist-tile__add');
                /* Sold-out tiles keep their disabled "Sold out" state — the
                   Add drawer can't resolve an unavailable variant either. */
                if (addBtn && !addBtn.disabled) {
                  addBtn.type = 'button';
                  addBtn.setAttribute('data-wl-add-trigger', '');
                  addBtn.setAttribute('data-product-handle', result.item.handle);
                  var label = addBtn.querySelector('span');
                  var selectLabel = addBtn.getAttribute('data-wl-select-label');
                  if (label && selectLabel) label.textContent = selectLabel;
                }
              }
              self.grid.appendChild(li);
            });
            if (self.loadingEl) self.loadingEl.setAttribute('hidden', '');
            reflectToggles(self.grid);
            /* #93 tile replacement: if a drawer close is waiting on the
               replacement tile's trigger, focus it now (hoisted fn). */
            restoreWlFocus();
          });
        }
      }
    );
  }

  /*
   * Variant selection controller for the drawer body (fetched partial).
   * Reuses the product-variant-picker CSS contract (option radios) rather than the
   * block-coupled snippet, since the drawer is not a PDP section block. Resolves the
   * selected variant against the embedded variants JSON, updates the price + selected
   * value labels, and gates the confirm button (aria-live size validation).
   */
  if (!customElements.get('wishlist-variant-form')) {
    customElements.define(
      'wishlist-variant-form',
      class extends HTMLElement {
        connectedCallback() {
          this.pid = this.getAttribute('data-product-id');
          this.handle = this.getAttribute('data-product-handle');
          this.optionCount = parseInt(this.getAttribute('data-option-count'), 10) || 0;
          var dataEl = this.querySelector('[data-wl-variants]');
          try {
            this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
          } catch (e) {
            this.variants = [];
          }
          this.priceEl = this.querySelector('[data-wl-price]');
          this.imageEl = this.querySelector('[data-wl-image]');
          this.errorEl = this.querySelector('[data-wl-size-error]');
          this.unavailableEl = this.querySelector('[data-wl-unavailable-msg]');
          this.statusEl = this.querySelector('[data-wl-status]');
          this.confirmBtn = this.querySelector('[data-wl-confirm]');
          this.qtyInput = this.querySelector('[data-wl-qty] input, input[name="quantity"]');
          this.selected = null;

          this.addEventListener('change', this._onChange.bind(this));
          if (this.confirmBtn) this.confirmBtn.addEventListener('click', this._onConfirm.bind(this));

          /* Pre-select a variant when editing. */
          var preset = this.getAttribute('data-variant-id');
          if (preset) this._preselect(preset);
          this._recompute();
        }

        get mode() {
          var drawer = this.closest('[data-wl-mode]');
          return drawer ? drawer.getAttribute('data-wl-mode') : 'add';
        }

        _selectedValues() {
          var values = [];
          for (var pos = 1; pos <= this.optionCount; pos++) {
            var checked = this.querySelector('input[data-option-position="' + pos + '"]:checked');
            values.push(checked ? checked.value : null);
          }
          return values;
        }

        _preselect(variantId) {
          var variant = this.variants.filter(function (v) {
            return String(v.id) === String(variantId);
          })[0];
          if (!variant || !variant.options) return;
          var self = this;
          variant.options.forEach(function (val, i) {
            var input = self.querySelector(
              'input[data-option-position="' + (i + 1) + '"][value="' + CSS.escape(val) + '"]'
            );
            if (input) input.checked = true;
          });
        }

        _onChange(e) {
          if (!e.target.matches('[data-option-position]')) return;
          /* Sold-out values stay focusable (aria-disabled, not disabled) but
             must not activate — revert to the last valid selection (#66). */
          if (e.target.hasAttribute('data-wl-unavailable')) {
            e.target.checked = false;
            var pos = e.target.getAttribute('data-option-position');
            var last = this._lastValid && this._lastValid[pos];
            if (last) {
              var prev = this.querySelector(
                'input[data-option-position="' + pos + '"][value="' + CSS.escape(last) + '"]'
              );
              if (prev) prev.checked = true;
            }
            /* QA P3 fix: fall through to _recompute so the selected-value
               labels and state marker resync even when nothing could be
               restored (incomplete prior selection). */
          }
          this._recompute();
        }

        _setState(state) {
          this.setAttribute('data-wl-state', state);
        }

        _recompute() {
          var values = this._selectedValues();
          var complete = values.every(function (v) {
            return v !== null;
          });
          this.selected = null;
          if (complete) {
            this.selected = this.variants.filter(function (v) {
              return (
                v.options &&
                v.options.length === values.length &&
                v.options.every(function (o, i) {
                  return o === values[i];
                })
              );
            })[0];
          }
          /* Update selected-value labels. */
          var self = this;
          this.querySelectorAll('[data-wl-selected-value]').forEach(function (el) {
            var pos = parseInt(el.getAttribute('data-wl-selected-value'), 10);
            el.textContent = values[pos - 1] || el.getAttribute('data-placeholder') || '';
          });
          /* Update price + image when a concrete variant resolves. */
          if (this.selected) {
            if (this.priceEl && this.selected.price) this.priceEl.textContent = this.selected.price;
            if (this.imageEl && this.selected.image) this.imageEl.src = this.selected.image;
          }
          var ready = this.optionCount === 0 || (complete && this.selected && this.selected.available !== false);
          if (this.confirmBtn) this.confirmBtn.disabled = !ready;
          if (ready && this.errorEl) this.errorEl.setAttribute('hidden', '');
          /* Track the last valid selection per position so a sold-out chip
             activation can restore it (_onChange). QA P3 fix: seed from ANY
             non-null value every pass — gating on ready||complete meant an
             incomplete selection (one of two groups picked) had nothing to
             restore, leaving the group empty while its label kept the old
             value. */
          this._lastValid = {};
          for (var i = 0; i < values.length; i++) {
            if (values[i] !== null) this._lastValid[String(i + 1)] = values[i];
          }
          /* State marker + variant-unavailable surface (states 8/9). */
          if (this.unavailableEl) {
            if (complete && !ready && this.optionCount > 0) {
              this.unavailableEl.removeAttribute('hidden');
            } else {
              this.unavailableEl.setAttribute('hidden', '');
            }
          }
          if (ready) {
            this._setState('selected');
          } else if (complete && this.optionCount > 0) {
            this._setState('unavailable');
          } else {
            this._setState('pristine');
          }
          void self;
        }

        _onConfirm(e) {
          e.preventDefault();
          if (this.optionCount > 0 && !this.selected) {
            if (this.errorEl) this.errorEl.removeAttribute('hidden');
            this._setState('error');
            return;
          }
          /* submitting: single-frame transient (the write is synchronous) —
             attributes applied for observability (state 4). */
          this._setState('submitting');
          this.setAttribute('aria-busy', 'true');
          this.querySelectorAll('[data-wl-chips]').forEach(function (group) {
            group.setAttribute('aria-disabled', 'true');
          });
          var qty = this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;
          Store.upsertVariant({
            pid: this.pid,
            handle: this.handle,
            id: this.selected ? this.selected.id : null,
            qty: qty,
          });
          /* success: polite announcement BEFORE the drawer closes (E3). */
          this.removeAttribute('aria-busy');
          this._setState('success');
          if (this.statusEl) {
            var label = this.querySelector('[data-wl-added-label]');
            this.statusEl.textContent = label ? label.textContent : '';
          }
          var drawer = this.closest('.drawer');
          if (drawer && typeof drawer.hide === 'function') drawer.hide();
        }
      }
    );
  }

  /* Drawer controller - opens Add / Edit drawers and fills them via Section Rendering. */
  var DRAWERS = { add: 'AddWishlistDrawer', edit: 'EditVariantDrawer' };

  /* Focus continuity across the #93 tile replacement: a confirm re-renders
     <wishlist-page>, replacing the very trigger held in drawer.activeElement,
     so the vendor's focus return lands on <body>. When that happens, re-target
     the SAME product's trigger on the replacement tile (selector recorded at
     hide time, applied again after the async grid render). */
  var pendingWlFocus = null; /* { el, selector, ts } */

  function restoreWlFocus() {
    if (!pendingWlFocus) return;
    if (Date.now() - pendingWlFocus.ts > 5000) {
      pendingWlFocus = null; /* stale — don't steal focus on a later render */
      return;
    }
    /* While the original trigger is still connected the vendor focus return
       covers it — but the grid render may STILL be in flight (it can outlast
       the 300ms hide transition), so keep the pending state armed; the
       post-render restoreWlFocus() call re-checks. */
    if (pendingWlFocus.el.isConnected) return;
    var el = document.querySelector(pendingWlFocus.selector);
    if (el) {
      pendingWlFocus = null;
      /* Vendor removeTrapFocus fires on a 0ms timeout after handleAfterHide
         and calls .focus() on the (disconnected) original trigger — a no-op —
         so queue behind it to win regardless of arrival order. */
      setTimeout(function () {
        el.focus();
      });
    }
  }

  document.addEventListener('drawer:handleAfterHide', function (e) {
    var drawer = e.target;
    if (!drawer._wlTrigger) return;
    pendingWlFocus = drawer._wlTrigger;
    pendingWlFocus.ts = Date.now();
    drawer._wlTrigger = null;
    restoreWlFocus(); /* replacement tile may already be mounted */
  });

  /* Visibility-filtered Tab wrap (search-drawer / quick-add precedent):
     vendor trapFocus derives first/last from ALL focusables, including
     hidden ones (the <1024 display:none qty stepper), so its wrap can fire
     from the wrong element. Element-level keydown fires before the vendor's
     document-level handler in the bubble phase; stopPropagation makes this
     the canonical wrap. Bound once per shell, travels through portalling. */
  function bindWlTabWrap(drawer) {
    if (drawer._wlTabWrap) return;
    drawer._wlTabWrap = true;
    drawer.addEventListener('keydown', function (event) {
      if (!event.code || event.code.toUpperCase() !== 'TAB' || !drawer.open) return;
      var a11y = window.FoxTheme && FoxTheme.a11y;
      if (!a11y) return;
      var visible = a11y.getFocusableElements(drawer).filter(function (el) {
        return el.offsetParent !== null || el.getClientRects().length > 0;
      });
      if (!visible.length) return;
      var first = visible[0];
      var last = visible[visible.length - 1];
      if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        event.stopPropagation();
        first.focus();
      } else if (event.shiftKey && (event.target === first || event.target === drawer)) {
        event.preventDefault();
        event.stopPropagation();
        last.focus();
      }
    });
  }

  function openWishlistDrawer(mode, trigger) {
    var drawer = document.getElementById(DRAWERS[mode]);
    if (!drawer) return;
    bindWlTabWrap(drawer);
    pendingWlFocus = null;
    var handle = trigger.getAttribute('data-product-handle');
    var variantId = trigger.getAttribute('data-variant-id');
    var body = drawer.querySelector('[data-wl-drawer-body]');
    if (!handle || !body) return;
    var formSection = body.getAttribute('data-form-section');
    if (!formSection) return;

    var esc = window.CSS && CSS.escape ? CSS.escape(handle) : handle;
    drawer._wlTrigger = {
      el: trigger,
      selector:
        '[data-wl-edit][data-product-handle="' + esc + '"],' +
        '[data-wl-add-trigger][data-product-handle="' + esc + '"]',
    };
    drawer.setAttribute('data-wl-mode', mode);
    body.setAttribute('aria-busy', 'true');

    fetchProductSection(handle, formSection, variantId)
      .then(function (html) {
        var node = parseFragment(html, '[data-wl-form-root]');
        body.innerHTML = '';
        if (node) {
          if (mode === 'edit' && variantId) node.setAttribute('data-variant-id', variantId);
          node.setAttribute('data-wl-form-mode', mode);
          body.appendChild(node);
        }
        body.removeAttribute('aria-busy');
        if (typeof drawer.show === 'function') drawer.show(trigger);
      })
      .catch(function () {
        body.removeAttribute('aria-busy');
      });
  }

  /* CAPTURE phase + stopPropagation (drawers brief Δ9): the tile's Edit button
     ships aria-controls="EditVariantDrawer", so after the drawer's first
     portal-restore cycle re-binds ModalComponent.controls, onButtonClick would
     show() the shell EMPTY ahead of the section fetch. Intercepting at capture
     keeps this the single opener (fetch-then-show); aria-haspopup stays on the
     trigger and aria-expanded still syncs via attributeChangedCallback. */
  document.addEventListener(
    'click',
    function (e) {
      var edit = e.target.closest('[data-wl-edit]');
      if (edit) {
        e.preventDefault();
        e.stopPropagation();
        openWishlistDrawer('edit', edit);
        return;
      }
      var add = e.target.closest('[data-wl-add-trigger]');
      if (add) {
        e.preventDefault();
        e.stopPropagation();
        openWishlistDrawer('add', add);
      }
    },
    true
  );

  /* Boot: hydrate from the (guarded) customer metafield, then reflect UI. */
  function boot() {
    var mfEl = document.querySelector('[data-wl-metafield]');
    if (mfEl) {
      try {
        Store.hydrateFromMetafield(JSON.parse(mfEl.textContent));
      } catch (e) {
        /* malformed metafield JSON - ignore, keep localStorage */
      }
    }
    reflectToggles(document);
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: Store.items() } }));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
